/**
 * DeterministicDecisionEngine — the smallest working Decision Engine.
 *
 * Pipeline:
 *   signals → clusterIntents → detectObservations → build candidates
 *   → apply DecisionPolicy[] (reject / boost / downweight / set_horizon)
 *   → score → align → rank → RankedOpportunity[] + RejectedCandidate[]
 *
 * No LLM. This is Retail Ranker's product logic. An LLM-assisted engine
 * later slots in behind the same DecisionEngine interface.
 */

import { clusterIntents } from "./cluster";
import { detectObservations } from "./observe";
import { DEFAULT_POLICIES } from "./policies";
import { shareStem } from "./text";
import type {
  AlignmentTrace,
  BusinessContextTags,
  ContextTag,
  CustomerIntent,
  DecisionEngine,
  DecisionEngineInput,
  DecisionEngineOutput,
  DecisionPolicy,
  DemandVisibilityGap,
  FiredPolicy,
  InsufficientEvidence,
  Level,
  Observation,
  OpportunityCandidate,
  OpportunityHorizon,
  OpportunityScores,
  RankedOpportunity,
  RejectedCandidate,
  TradeoffTrace,
} from "./types";

const WEIGHTS_VERSION = "weights@0.1.0";

function levelize(n: number, low: number, high: number): Level {
  if (n >= high) return "high";
  if (n >= low) return "medium";
  return "low";
}

function horizonFromGap(
  gap: DemandVisibilityGap | undefined,
  intent: CustomerIntent,
): OpportunityHorizon {
  if (intent.trend === "rising" && intent.local_confidence >= 0.7) return "this_week";
  if ((gap?.gap_magnitude ?? 0) >= 0.5) return "this_season";
  return "this_year";
}

function matchAlignment(
  intent: CustomerIntent,
  ctx: BusinessContextTags,
): AlignmentTrace {
  const hay = `${intent.label} ${intent.description} ${(intent.tags ?? []).join(" ")}`;
  const matched_goals: ContextTag[] = ctx.goals.filter((g) => shareStem(hay, g.label));
  const matched_strengths: ContextTag[] = ctx.strengths.filter((s) => shareStem(hay, s.label));
  return { matched_goals, matched_strengths };
}

function affectsFor(presence: OpportunityCandidate["presence"]): RankedOpportunity["affects"] {
  if (presence === "absent" || presence === "thin") return ["website", "merchandising"];
  return ["merchandising", "email"];
}

export class DeterministicDecisionEngine implements DecisionEngine {
  readonly name = "deterministic-decision-engine";
  readonly version = "0.1.0";

  constructor(private policies: DecisionPolicy[] = DEFAULT_POLICIES) {}

  async analyze(input: DecisionEngineInput): Promise<DecisionEngineOutput> {
    const { business_context: ctx, signals } = input;

    // --- Analysis Layer -----------------------------------------------
    const intents = clusterIntents(signals);
    const observations = detectObservations(signals, intents);

    // --- Build candidates from intents + observations -----------------
    const observationsByIntent = new Map<string, Observation[]>();
    for (const o of observations) {
      if (!o.intent_id) continue;
      if (!observationsByIntent.has(o.intent_id)) observationsByIntent.set(o.intent_id, []);
      observationsByIntent.get(o.intent_id)!.push(o);
    }

    let candidates: OpportunityCandidate[] = intents.map((intent) => {
      const obs = observationsByIntent.get(intent.intent_id) ?? [];
      const gap = obs.find((o) => o.kind === "demand_visibility_gap") as
        | DemandVisibilityGap
        | undefined;
      const presence = gap?.presence ?? "absent";
      return {
        candidate_id: `cand:${intent.intent_id}`,
        intent,
        observations: obs,
        presence,
        affects: affectsFor(presence),
        policies_fired: [],
        score_raw: gap?.gap_magnitude ?? intent.demand_strength * 0.3,
        boosts: 0,
        downweights: 0,
      };
    });

    // --- Apply DecisionPolicies ---------------------------------------
    for (const policy of this.policies) {
      for (const c of candidates) {
        if (c.rejected) continue;
        if (!policy.applies_to(c, ctx)) continue;
        const fired: FiredPolicy = {
          policy_id: policy.policy_id,
          version: policy.version,
          effect: policy.effect,
        };
        c.policies_fired.push(fired);

        switch (policy.effect.kind) {
          case "reject":
            c.rejected = {
              reason_code: policy.effect.reason_code,
              detail: { metrics: { score_before_reject: c.score_raw } },
            };
            break;
          case "boost":
            c.boosts += policy.effect.delta;
            break;
          case "downweight":
            c.downweights += policy.effect.delta;
            break;
          case "set_horizon":
            c.horizon_override = policy.effect.horizon;
            break;
        }
      }
    }

    const rejected: RejectedCandidate[] = candidates
      .filter((c) => c.rejected)
      .map((c) => ({
        candidate_id: c.candidate_id,
        intent_id: c.intent.intent_id,
        intent_label: c.intent.label,
        source_observations: c.observations.map((o) => o.observation_id),
        reason_code: c.rejected!.reason_code,
        reason_detail: c.rejected!.detail,
        policies_fired: c.policies_fired,
      }));

    candidates = candidates.filter((c) => !c.rejected);

    // --- Score --------------------------------------------------------
    const ranked: RankedOpportunity[] = candidates.map((c) => {
      const gap = c.observations.find((o) => o.kind === "demand_visibility_gap") as
        | DemandVisibilityGap
        | undefined;

      const alignment = matchAlignment(c.intent, ctx);

      const impact_raw =
        (gap?.gap_magnitude ?? c.intent.demand_strength * 0.3) +
        alignment.matched_goals.length * 0.25 +
        alignment.matched_strengths.length * 0.15;

      const effort_raw =
        (c.presence === "absent" ? 0.7 : c.presence === "thin" ? 0.4 : 0.2) +
        (c.affects.length - 1) * 0.1;

      const confidence_raw =
        c.intent.local_confidence * 0.6 +
        Math.min(1, c.intent.evidence.length / 3) * 0.4;

      const priority_raw =
        impact_raw * 1.0 +
        c.boosts -
        c.downweights -
        effort_raw * 0.3 +
        confidence_raw * 0.3;

      const scores: OpportunityScores = {
        impact: levelize(impact_raw, 0.4, 0.8),
        effort: levelize(effort_raw, 0.4, 0.7),
        confidence: levelize(confidence_raw, 0.4, 0.7),
        numeric: { impact_raw, effort_raw, confidence_raw, priority_raw },
        weights_version: WEIGHTS_VERSION,
      };

      const horizon: OpportunityHorizon =
        c.horizon_override ?? horizonFromGap(gap, c.intent);

      return {
        opportunity_id: `opp:${c.intent.intent_id}`,
        priority_rank: 0, // assigned after sort
        horizon,
        intent_id: c.intent.intent_id,
        intent_label: c.intent.label,
        intent_description: c.intent.description,
        source_observations: c.observations.map((o) => o.observation_id),
        scores,
        alignment,
        tradeoffs: { ranked_above: [] }, // filled after sort
        policies_fired: c.policies_fired,
        affects: c.affects,
      };
    });

    // --- Rank + tradeoffs --------------------------------------------
    ranked.sort((a, b) => b.scores.numeric.priority_raw - a.scores.numeric.priority_raw);
    ranked.forEach((op, i) => (op.priority_rank = i + 1));

    if (ranked.length >= 2) {
      const top = ranked[0];
      const runnersUp = ranked.slice(1, 4);
      top.tradeoffs.ranked_above = runnersUp.map((r) => {
        const reason = pickTradeoffReason(top, r);
        return {
          opportunity_id: r.opportunity_id,
          reason_code: reason.code,
          delta: reason.delta,
        };
      });
      top.tradeoffs.ranked_first_because = renderRankedFirst(top, runnersUp[0]);
    }

    // --- Insufficient evidence ---------------------------------------
    const insufficient: InsufficientEvidence[] = [];
    if (intents.length === 0) {
      insufficient.push({
        area: "demand_intelligence",
        reason_code: "provider_returned_empty",
        what_would_unblock_it: { action: "connect_provider", provider: "demand" },
      });
    }
    if (
      signals.filter((s) => s.kind === "visibility.site_presence").length === 0
    ) {
      insufficient.push({
        area: "visibility",
        reason_code: "no_provider_connected",
        what_would_unblock_it: { action: "add_website" },
      });
    }
    if (ranked.length === 0 && intents.length > 0) {
      insufficient.push({
        area: "opportunities",
        reason_code: "constraints_exclude_all_candidates",
        what_would_unblock_it: { action: "update_context" },
      });
    }

    return {
      engine_version: `${this.name}@${this.version}`,
      weights_version: WEIGHTS_VERSION,
      policy_versions: this.policies.map((p) => ({ policy_id: p.policy_id, version: p.version })),
      intents,
      observations,
      opportunities: ranked,
      rejected,
      insufficient_evidence: insufficient,
    };
  }
}

function pickTradeoffReason(
  top: RankedOpportunity,
  other: RankedOpportunity,
): { code: TradeoffTrace["ranked_above"][number]["reason_code"]; delta: number } {
  const diffs: Array<{ code: TradeoffTrace["ranked_above"][number]["reason_code"]; delta: number }> = [
    { code: "higher_gap_magnitude", delta: top.scores.numeric.impact_raw - other.scores.numeric.impact_raw },
    { code: "stronger_goal_alignment", delta: top.alignment.matched_goals.length - other.alignment.matched_goals.length },
    { code: "higher_confidence", delta: top.scores.numeric.confidence_raw - other.scores.numeric.confidence_raw },
    { code: "lower_effort", delta: other.scores.numeric.effort_raw - top.scores.numeric.effort_raw },
    { code: "shorter_horizon", delta: horizonRank(top.horizon) - horizonRank(other.horizon) },
  ];
  diffs.sort((a, b) => b.delta - a.delta);
  return diffs[0];
}

function horizonRank(h: OpportunityHorizon): number {
  return h === "this_week" ? 3 : h === "this_season" ? 2 : 1;
}

function renderRankedFirst(top: RankedOpportunity, next: RankedOpportunity): string {
  const reason = pickTradeoffReason(top, next);
  const map: Record<TradeoffTrace["ranked_above"][number]["reason_code"], string> = {
    higher_gap_magnitude: "the demand-vs-visibility gap is larger than any other candidate",
    stronger_goal_alignment: "it aligns most directly with a stated business goal",
    higher_confidence: "the evidence behind it is stronger and more local",
    lower_effort: "it costs less effort to act on for a comparable payoff",
    shorter_horizon: "it is time-sensitive and other candidates can wait",
  };
  return `${top.intent_label} ranks first because ${map[reason.code]}.`;
}